import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";

import { Banner, Button, TextField, focusFirstInvalid } from "@ui/react";
import { auth } from "../../lib/firebase";
import { firebaseAuthMessage, firebaseErrorCode } from "./firebaseErrors";
import { OnboardingLayout } from "./OnboardingLayout";

export function ForgotPasswordScreen() {
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email.trim()) {
      setFieldError("Enter your email address.");
      focusFirstInvalid(formRef.current);
      return;
    }
    if (!email.includes("@")) {
      setFieldError("Enter a valid email address.");
      focusFirstInvalid(formRef.current);
      return;
    }
    setFieldError(null);

    setSubmitting(true);
    setFormError(null);
    try {
      await sendPasswordResetEmail(auth(), email.trim());
      setSent(true);
    } catch (error) {
      // "No such account" is reported as success on purpose: telling a stranger
      // which addresses have Shoku accounts is an enumeration oracle, and newer
      // Firebase projects suppress the code for exactly that reason anyway.
      if (firebaseErrorCode(error) === "auth/user-not-found") setSent(true);
      else setFormError(firebaseAuthMessage(error, "We couldn't send the reset link. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <OnboardingLayout
        eyebrow="Check your inbox"
        title="We've sent a reset link."
        lead="Follow the link in the email to choose a new password."
      >
        <div className="stack">
          <Banner tone="info" title="Not there yet?">
            It can take a minute to arrive. Mail from Shoku is often filtered into spam or
            promotions — check those folders before requesting another link.
          </Banner>
          <p className="muted">
            We sent it to <strong>{email.trim()}</strong>.
          </p>
          <Link className="btn btn-primary btn-lg btn-block" to="/login">
            Back to sign in
          </Link>
          <Button variant="tertiary" block onClick={() => setSent(false)}>
            Use a different email address
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      eyebrow="Reset password"
      title="Forgot your password?"
      lead="Enter the email linked to your Shoku contractor account and we'll send you a reset link."
      footer={
        <p className="muted">
          Remembered it?{" "}
          <Link className="btn btn-tertiary" to="/login">
            Back to sign in
          </Link>
        </p>
      }
    >
      <form ref={formRef} className="form" onSubmit={onSubmit} noValidate>
        {formError ? <Banner tone="danger" title="Couldn't send the link">{formError}</Banner> : null}

        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder="you@somewhere.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldError}
          autoFocus
          required
        />

        <Button type="submit" size="lg" loading={submitting}>
          Send reset link
        </Button>
      </form>
    </OnboardingLayout>
  );
}
